/**
 * vibecheck doctor — Diagnose project setup and environment
 */

import * as fs from 'fs';
import * as path from 'path';
import { execSync } from 'child_process';
import type { Command } from 'commander';
import type { GlobalOptions, DoctorCheck, CheckStatus } from '../types';
import { detectProject } from '../core/project-detect';
import * as out from '../ui/output';
import { colors, icons } from '../ui/theme';

export function registerDoctor(program: Command): void {
  const cmd = program
    .command('doctor')
    .description('Diagnose project setup and environment issues')
    .option('--fix', 'Attempt to auto-fix found issues')
    .option('--verbose', 'Show all checks including passing ones');

  cmd.command('check').description('Run all diagnostics (default)').action(async (_o: any, c: Command) => {
    await runDoctor({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts()) });
  });

  cmd.command('env').description('Check environment variables').action(async (_o: any, c: Command) => {
    await runDoctor({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts()), focus: 'env' });
  });

  cmd.command('deps').description('Check dependencies').action(async (_o: any, c: Command) => {
    await runDoctor({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts()), focus: 'deps' });
  });

  cmd.command('config').description('Validate VibeCheck configuration').action(async (_o: any, c: Command) => {
    await runDoctor({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts()), focus: 'config' });
  });

  cmd.action(async (opts: any, command: Command) => {
    await runDoctor({ ...(command.parent!.opts() as GlobalOptions), ...opts });
  });
}

interface DoctorOpts extends GlobalOptions {
  fix?: boolean;
  focus?: 'env' | 'deps' | 'config';
}

async function runDoctor(opts: DoctorOpts): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();
  const checks: DoctorCheck[] = [];

  out.brandHeader('Doctor', 'Project diagnostics');
  out.nl();

  // 1. Node.js version
  const nodeVersion = process.version;
  const nodeMajor = parseInt(nodeVersion.slice(1));
  checks.push({
    name: 'Node.js',
    status: nodeMajor >= 18 ? 'passed' : nodeMajor >= 16 ? 'warning' : 'failed',
    value: `${nodeVersion} ${nodeMajor >= 18 ? '(supported)' : '(upgrade recommended)'}`,
  });

  // 2. Package manager
  const project = detectProject(rootPath);
  let pmVersion = '';
  try { pmVersion = execSync(`${project.packageManager} --version`, { encoding: 'utf-8', timeout: 5000 }).trim(); } catch { /* ignore */ }
  checks.push({
    name: 'Package manager',
    status: pmVersion ? 'passed' : 'warning',
    value: pmVersion ? `${project.packageManager} ${pmVersion}` : `${project.packageManager} (version unknown)`,
  });

  // 3. TypeScript
  if (fs.existsSync(path.join(rootPath, 'tsconfig.json'))) {
    let tsVersion = '';
    try {
      const tsPkg = path.join(rootPath, 'node_modules', 'typescript', 'package.json');
      if (fs.existsSync(tsPkg)) {
        tsVersion = JSON.parse(fs.readFileSync(tsPkg, 'utf-8')).version;
      }
    } catch { /* ignore */ }

    let strictMode = false;
    try {
      const tsconfig = JSON.parse(fs.readFileSync(path.join(rootPath, 'tsconfig.json'), 'utf-8'));
      strictMode = tsconfig.compilerOptions?.strict === true;
    } catch { /* ignore */ }

    checks.push({
      name: 'TypeScript',
      status: tsVersion ? 'passed' : 'warning',
      value: `${tsVersion || 'not installed'} ${strictMode ? '(strict mode ✓)' : '(strict mode off)'}`,
    });
  }

  // 4. Dependencies
  const nodeModules = path.join(rootPath, 'node_modules');
  if (fs.existsSync(nodeModules)) {
    let depCount = 0;
    try {
      const entries = fs.readdirSync(nodeModules);
      depCount = entries.filter(e => !e.startsWith('.')).length;
    } catch { /* ignore */ }
    checks.push({
      name: 'Dependencies',
      status: depCount > 0 ? 'passed' : 'failed',
      value: `${depCount} packages installed`,
      message: depCount === 0 ? `Run '${project.packageManager} install' to install dependencies` : undefined,
    });
  } else {
    checks.push({
      name: 'Dependencies',
      status: 'failed',
      value: 'node_modules not found',
      message: `Run '${project.packageManager} install' to install dependencies`,
    });
  }

  // 5. Environment variables
  if (!opts.focus || opts.focus === 'env') {
    const envExample = path.join(rootPath, '.env.example');
    const envFile = path.join(rootPath, '.env');
    if (fs.existsSync(envExample)) {
      const exampleVars = parseEnvFile(envExample);
      const actualVars = fs.existsSync(envFile) ? parseEnvFile(envFile) : new Set<string>();
      const missing = [...exampleVars].filter(v => !actualVars.has(v));
      if (missing.length > 0) {
        checks.push({
          name: 'Environment',
          status: 'failed',
          value: `${missing.length} missing variables`,
          message: missing.map(v => `${v} is in .env.example but not in .env`).join('\n     '),
        });
      } else {
        checks.push({ name: 'Environment', status: 'passed', value: `${exampleVars.size} vars configured` });
      }
    } else {
      checks.push({ name: 'Environment', status: 'passed', value: 'no .env.example found' });
    }
  }

  // 6. Git
  let gitStatus: CheckStatus = 'passed';
  let gitValue = '';
  try {
    const branch = execSync('git branch --show-current', { cwd: rootPath, encoding: 'utf-8', timeout: 5000 }).trim();
    const status = execSync('git status --porcelain', { cwd: rootPath, encoding: 'utf-8', timeout: 5000 }).trim();
    const isClean = status.length === 0;
    gitValue = `${isClean ? 'clean' : 'dirty'}, on branch ${branch}`;
    gitStatus = isClean ? 'passed' : 'warning';
  } catch {
    gitValue = 'not a git repository';
    gitStatus = 'warning';
  }
  checks.push({ name: 'Git', status: gitStatus, value: gitValue });

  // 7. VibeCheck config
  if (!opts.focus || opts.focus === 'config') {
    const configPath = path.join(rootPath, '.vibecheckrc.json');
    if (fs.existsSync(configPath)) {
      try {
        JSON.parse(fs.readFileSync(configPath, 'utf-8'));
        checks.push({ name: 'VibeCheck config', status: 'passed', value: 'valid' });
      } catch {
        checks.push({ name: 'VibeCheck config', status: 'failed', value: 'invalid JSON', message: 'Fix the JSON syntax in .vibecheckrc.json' });
      }
    } else {
      checks.push({ name: 'VibeCheck config', status: 'warning', value: 'not found', message: "Run 'vibecheck kickoff' to create one" });
    }
  }

  // 8. Disk space
  try {
    const stats = fs.statfsSync(rootPath);
    const freeGB = (stats.bavail * stats.bsize) / (1024 * 1024 * 1024);
    checks.push({
      name: 'Disk space',
      status: freeGB > 1 ? 'passed' : 'warning',
      value: `${freeGB.toFixed(0)}GB available`,
    });
  } catch {
    checks.push({ name: 'Disk space', status: 'passed', value: 'check skipped' });
  }

  // 9. ESLint configuration
  if (!opts.focus || opts.focus === 'config') {
    const eslintConfigs = ['.eslintrc', '.eslintrc.js', '.eslintrc.json', '.eslintrc.yml', '.eslintrc.cjs', 'eslint.config.js', 'eslint.config.mjs', 'eslint.config.ts'];
    const hasEslint = eslintConfigs.some(f => fs.existsSync(path.join(rootPath, f)));
    const pkgHasEslint = (() => { try { const pkg = JSON.parse(fs.readFileSync(path.join(rootPath, 'package.json'), 'utf-8')); return !!pkg.eslintConfig; } catch { return false; } })();
    checks.push({
      name: 'ESLint',
      status: hasEslint || pkgHasEslint ? 'passed' : 'warning',
      value: hasEslint || pkgHasEslint ? 'configured' : 'not configured',
      message: !hasEslint && !pkgHasEslint ? 'Add ESLint for consistent code quality' : undefined,
    });
  }

  // 10. Prettier / formatter
  if (!opts.focus || opts.focus === 'config') {
    const prettierConfigs = ['.prettierrc', '.prettierrc.js', '.prettierrc.json', '.prettierrc.yml', '.prettierrc.cjs', 'prettier.config.js', 'prettier.config.mjs'];
    const hasPrettier = prettierConfigs.some(f => fs.existsSync(path.join(rootPath, f)));
    const pkgHasPrettier = (() => { try { const pkg = JSON.parse(fs.readFileSync(path.join(rootPath, 'package.json'), 'utf-8')); return !!pkg.prettier; } catch { return false; } })();
    const hasBiome = fs.existsSync(path.join(rootPath, 'biome.json')) || fs.existsSync(path.join(rootPath, 'biome.jsonc'));
    checks.push({
      name: 'Code formatter',
      status: hasPrettier || pkgHasPrettier || hasBiome ? 'passed' : 'warning',
      value: hasBiome ? 'Biome' : hasPrettier || pkgHasPrettier ? 'Prettier' : 'not configured',
      message: !hasPrettier && !pkgHasPrettier && !hasBiome ? 'Add Prettier or Biome for consistent formatting' : undefined,
    });
  }

  // 11. CI/CD configuration
  {
    const ciConfigs = [
      { path: '.github/workflows', name: 'GitHub Actions' },
      { path: '.gitlab-ci.yml', name: 'GitLab CI' },
      { path: '.circleci/config.yml', name: 'CircleCI' },
      { path: 'Jenkinsfile', name: 'Jenkins' },
      { path: '.travis.yml', name: 'Travis CI' },
      { path: 'bitbucket-pipelines.yml', name: 'Bitbucket Pipelines' },
      { path: 'vercel.json', name: 'Vercel' },
      { path: 'netlify.toml', name: 'Netlify' },
      { path: 'railway.toml', name: 'Railway' },
    ];
    const found = ciConfigs.filter(c => fs.existsSync(path.join(rootPath, c.path)));
    checks.push({
      name: 'CI/CD',
      status: found.length > 0 ? 'passed' : 'warning',
      value: found.length > 0 ? found.map(f => f.name).join(', ') : 'not configured',
      message: found.length === 0 ? 'Add CI/CD for automated testing and deployment' : undefined,
    });
  }

  // 12. Testing framework
  {
    const testFrameworks = [
      { dep: 'vitest', name: 'Vitest' },
      { dep: 'jest', name: 'Jest' },
      { dep: 'mocha', name: 'Mocha' },
      { dep: '@playwright/test', name: 'Playwright' },
      { dep: 'cypress', name: 'Cypress' },
      { dep: 'ava', name: 'AVA' },
    ];
    const found = testFrameworks.filter(t => project.dependencies.includes(t.dep) || project.devDependencies.includes(t.dep));
    checks.push({
      name: 'Test framework',
      status: found.length > 0 ? 'passed' : 'warning',
      value: found.length > 0 ? found.map(f => f.name).join(', ') : 'none detected',
      message: found.length === 0 ? 'Add a testing framework (vitest, jest, playwright)' : undefined,
    });
  }

  // 13. Lockfile check
  {
    const lockFiles = [
      { file: 'pnpm-lock.yaml', pm: 'pnpm' },
      { file: 'yarn.lock', pm: 'yarn' },
      { file: 'package-lock.json', pm: 'npm' },
      { file: 'bun.lockb', pm: 'bun' },
    ];
    const found = lockFiles.filter(l => fs.existsSync(path.join(rootPath, l.file)));
    if (found.length === 0) {
      checks.push({ name: 'Lockfile', status: 'warning', value: 'missing', message: `Run '${project.packageManager} install' to generate a lockfile` });
    } else if (found.length > 1) {
      checks.push({ name: 'Lockfile', status: 'warning', value: `multiple: ${found.map(f => f.file).join(', ')}`, message: 'Multiple lockfiles can cause conflicts. Remove extras.' });
    } else if (found[0].pm !== project.packageManager) {
      checks.push({ name: 'Lockfile', status: 'warning', value: `${found[0].file} (but using ${project.packageManager})`, message: `Lockfile doesn't match package manager` });
    } else {
      checks.push({ name: 'Lockfile', status: 'passed', value: found[0].file });
    }
  }

  // 14. Docker
  {
    const hasDockerfile = fs.existsSync(path.join(rootPath, 'Dockerfile')) || fs.existsSync(path.join(rootPath, 'docker-compose.yml')) || fs.existsSync(path.join(rootPath, 'docker-compose.yaml'));
    const hasDockerIgnore = fs.existsSync(path.join(rootPath, '.dockerignore'));
    if (hasDockerfile) {
      checks.push({
        name: 'Docker',
        status: hasDockerIgnore ? 'passed' : 'warning',
        value: hasDockerIgnore ? 'Dockerfile + .dockerignore' : 'Dockerfile found',
        message: !hasDockerIgnore ? 'Add .dockerignore to exclude node_modules, .git, etc.' : undefined,
      });
    }
  }

  // 15. Security headers (for web projects)
  if (project.framework.includes('Next') || project.framework.includes('Nuxt') || project.framework.includes('React') || project.framework.includes('Vue')) {
    const hasSecurityHeaders = (() => {
      // Check next.config.js for headers
      const nextConfig = path.join(rootPath, 'next.config.js');
      const nextConfigMjs = path.join(rootPath, 'next.config.mjs');
      const nextConfigTs = path.join(rootPath, 'next.config.ts');
      for (const cfg of [nextConfig, nextConfigMjs, nextConfigTs]) {
        if (fs.existsSync(cfg)) {
          try {
            const content = fs.readFileSync(cfg, 'utf-8');
            return content.includes('headers') || content.includes('Content-Security-Policy') || content.includes('X-Frame-Options');
          } catch { return false; }
        }
      }
      // Check for helmet in deps
      return project.dependencies.includes('helmet') || project.devDependencies.includes('helmet');
    })();
    checks.push({
      name: 'Security headers',
      status: hasSecurityHeaders ? 'passed' : 'warning',
      value: hasSecurityHeaders ? 'configured' : 'not detected',
      message: !hasSecurityHeaders ? 'Add security headers (CSP, X-Frame-Options, etc.) via next.config.js or helmet' : undefined,
    });
  }

  // 16. Monorepo detection
  {
    const isMonorepo = fs.existsSync(path.join(rootPath, 'pnpm-workspace.yaml')) || fs.existsSync(path.join(rootPath, 'lerna.json')) || fs.existsSync(path.join(rootPath, 'nx.json')) || fs.existsSync(path.join(rootPath, 'turbo.json'));
    if (isMonorepo) {
      const hasTurbo = fs.existsSync(path.join(rootPath, 'turbo.json'));
      const hasNx = fs.existsSync(path.join(rootPath, 'nx.json'));
      const hasLerna = fs.existsSync(path.join(rootPath, 'lerna.json'));
      const tool = hasTurbo ? 'Turborepo' : hasNx ? 'Nx' : hasLerna ? 'Lerna' : 'pnpm workspaces';
      checks.push({ name: 'Monorepo', status: 'passed', value: tool });
    }
  }

  // 17. Outdated Node.js check
  {
    const nvmrcPath = path.join(rootPath, '.nvmrc');
    const nodeVersionPath = path.join(rootPath, '.node-version');
    const hasNodeVersionFile = fs.existsSync(nvmrcPath) || fs.existsSync(nodeVersionPath);
    const pkgEngines = (() => { try { return JSON.parse(fs.readFileSync(path.join(rootPath, 'package.json'), 'utf-8')).engines?.node; } catch { return null; } })();
    checks.push({
      name: 'Node version pinning',
      status: hasNodeVersionFile || pkgEngines ? 'passed' : 'warning',
      value: hasNodeVersionFile ? '.nvmrc / .node-version found' : pkgEngines ? `engines.node: ${pkgEngines}` : 'not pinned',
      message: !hasNodeVersionFile && !pkgEngines ? 'Pin Node.js version with .nvmrc, .node-version, or package.json engines field' : undefined,
    });
  }

  const duration = performance.now() - start;

  if (opts.json) {
    out.jsonOutput({ checks, duration });
    return;
  }

  // Pretty output
  for (const check of checks) {
    const statusStr = check.value || check.status;
    const coloredStatus = check.status === 'passed' ? colors.success(statusStr)
      : check.status === 'warning' ? colors.warning(statusStr)
      : check.status === 'failed' ? colors.error(statusStr)
      : colors.dim(statusStr);
    console.log(out.checkLine(check.status, check.name, coloredStatus));
    if (check.message && check.status !== 'passed') {
      for (const line of check.message.split('\n')) {
        console.log(`     ${colors.dim(icons.arrow)} ${line}`);
      }
    }
  }

  out.nl();
  console.log(out.divider());

  const issues = checks.filter(c => c.status === 'failed' || c.status === 'warning');
  if (issues.length === 0) {
    out.success('Everything looks good — your project is healthy!');
  } else {
    const failed = checks.filter(c => c.status === 'failed').length;
    const warned = checks.filter(c => c.status === 'warning').length;
    const parts: string[] = [];
    if (failed > 0) parts.push(`${failed} issue${failed > 1 ? 's' : ''}`);
    if (warned > 0) parts.push(`${warned} warning${warned > 1 ? 's' : ''}`);
    console.log(`Result: ${parts.join(', ')} found`);
    if (opts.fix === undefined) {
      console.log(`\nRun ${colors.info("vibecheck doctor check --fix")} to attempt auto-fixes.`);
    }
  }

  out.nl();
  console.log(out.completedIn(duration));
}

function parseEnvFile(filePath: string): Set<string> {
  const vars = new Set<string>();
  try {
    for (const line of fs.readFileSync(filePath, 'utf-8').split('\n')) {
      const match = line.match(/^([A-Z_][A-Z0-9_]*)=/);
      if (match) vars.add(match[1]);
    }
  } catch { /* ignore */ }
  return vars;
}
